import * as React from "react"
import { jsonFetch } from "../bookingApi"
import type { AddressLookupOption } from "../types"
import type { useBrowserSession } from "./useBrowserSession"
import type { useCheckoutModel } from "./useCheckoutModel"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "quote" | "postcode" | "journeyStep" | "confirmationToken" | "addressLookupPostcode" | "setSelectedAddressId" | "setSelectedDeliveryAddress" | "setManualDeliveryEntry" | "setManualDeliveryAddress" | "setAddressLookupMessage" | "addressLookupCache" | "setAddressOptions" | "setAddressLookupPhase" | "addressLookupController">
    & Pick<ReturnType<typeof useCheckoutModel>,
        "baseUrl">
    & Pick<ReturnType<typeof useBrowserSession>,
        "browserSessionId">

export function useAddressLookup(inputs: Inputs) {
    const {
        quote,
        postcode,
        journeyStep,
        confirmationToken,
        addressLookupPostcode,
        setSelectedAddressId,
        setSelectedDeliveryAddress,
        setManualDeliveryEntry,
        setManualDeliveryAddress,
        setAddressLookupMessage,
        addressLookupCache,
        setAddressOptions,
        setAddressLookupPhase,
        addressLookupController,
        baseUrl,
        browserSessionId,
    } = inputs
    React.useEffect(() => {
        const deliveryPostcode = quote?.postcode || postcode.trim().toUpperCase()
        if (journeyStep !== "payment" || !confirmationToken || !deliveryPostcode) return
        if (addressLookupPostcode.current !== deliveryPostcode) {
            addressLookupPostcode.current = deliveryPostcode
            setSelectedAddressId("")
            setSelectedDeliveryAddress(null)
            setManualDeliveryEntry(false)
            setManualDeliveryAddress({ line1: "", line2: "", city: "" })
            setAddressLookupMessage("")
        }
        const cached = addressLookupCache.current.get(deliveryPostcode)
        if (cached) {
            setAddressOptions(cached)
            setAddressLookupPhase("ready")
            if (cached.length === 0) {
                setAddressLookupMessage("No addresses were found for this postcode. Enter the address manually.")
                setManualDeliveryEntry(true)
            }
            return
        }

        const controller = new AbortController()
        addressLookupController.current?.abort()
        addressLookupController.current = controller
        setAddressOptions([])
        setAddressLookupPhase("loading")
        setAddressLookupMessage("")
        jsonFetch<{ postcode: string; addresses: AddressLookupOption[] }>(`${baseUrl}/v1/address-lookup`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ confirmationToken, browserSessionId }),
            signal: controller.signal,
        }).then((result) => {
            addressLookupCache.current.set(deliveryPostcode, result.addresses)
            setAddressOptions(result.addresses)
            setAddressLookupPhase("ready")
            if (result.addresses.length === 0) {
                setAddressLookupMessage("No addresses were found for this postcode. Enter the address manually.")
                setManualDeliveryEntry(true)
            }
        }).catch((reason) => {
            if (reason.name === "AbortError") return
            setAddressOptions([])
            setAddressLookupPhase("error")
            setAddressLookupMessage(reason instanceof Error ? reason.message : "Address search is unavailable. Enter the address manually.")
            setManualDeliveryEntry(true)
        })
        return () => controller.abort()
    }, [baseUrl, browserSessionId, confirmationToken, journeyStep, postcode, quote?.postcode])
}
